import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CompletedLoan } from '../models/completed-loan';
import { InvoicedLoan } from '../models/invoiced-loan';
import { protectedResources } from '../auth-config';
import { environment } from 'src/environments/environment';
import { ToastService } from './toast.service';
import { InvoicePeriod } from '../models/invoice-period';
import { InvoicingReportLoan } from '../models/base-invoicing-loan';
import { InvoicingDetails } from '../models/invoicing-details';
import { ContractMetricExport } from '../models/contract-metric-export';

@Injectable()
export class InvoicingService {
  private baseUrl: string = protectedResources.LOSTalkerAPI.endpoint;
  private actionUrl = '';
  private Url = '';
  private headers = new HttpHeaders();
  private stripData = false;

  private errorHandler(error: HttpErrorResponse, ts: ToastService): Observable<any> {
    ts.toastCreate('An HTTP transport error occured: ' + error.url + ' ' + error.status, 'Warning', {
      keepAfterRouteChange: true,
    });

    if (environment.enableDebuggingTools) {
      console.error('An HTTP transport error occured: ' + error.error);
    }

    return throwError(() => error);
  }

  constructor(private http: HttpClient, private toastService: ToastService) 
  { 
    if(!environment.production){
      this.stripData = true;
    }
  }

  /**
   * Get Completed Loans for Invoice Period
   * @param invoicePeriodId
   * @returns CompletedLoan[]
   */
  getAllCompletedLoansByPeriod(invoicePeriodId: string): Observable<CompletedLoan[]> {
    this.actionUrl = 'CompletedLoans/';
    this.Url = this.baseUrl.concat(this.actionUrl, invoicePeriodId, '/', this.stripData.toString());
    return this.http
      .get<CompletedLoan[]>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  /**
   * Get Completed Loans for Invoice Period List
   * @param invoicePeriodId
   * @returns CompletedLoan[]
   */
   getAllCompletedLoansByClientForList(invoicePeriodIdList: string[]): Observable<CompletedLoan[]> {
    this.actionUrl = 'CompletedLoans/List';
    this.Url = this.baseUrl.concat(this.actionUrl, '/', this.stripData.toString());

    const params = new HttpParams({
      fromObject: {'invoicePeriodIdList': invoicePeriodIdList},
    });

    return this.http
      .get<CompletedLoan[]>(this.Url, { params })
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  /**
   * Get Invoiced Loans for Invoice Period
   * @param invoicePeriodId
   * @returns InvoicedLoan[]
   */
   getAllInvoicedLoansByClientForList(invoicePeriodIdList: string[]): Observable<InvoicedLoan[]> {
    this.actionUrl = 'InvoicedLoans/List';
    this.Url = this.baseUrl.concat(this.actionUrl, '/', this.stripData.toString());

    const params = new HttpParams({
      fromObject: {'invoicePeriodIdList': invoicePeriodIdList},
    });

    return this.http
      .get<InvoicedLoan[]>(this.Url, { params })
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  /**
   * Get Invoiced Loans for Invoice Period
   * @param invoicePeriodId
   * @returns InvoicedLoan[]
   */
  getAllInvoicedLoansByPeriod(invoicePeriodId: string): Observable<InvoicedLoan[]> {
    this.actionUrl = 'InvoicedLoans/';
    this.Url = this.baseUrl.concat(this.actionUrl, invoicePeriodId, '/', this.stripData.toString());
    return this.http
      .get<InvoicedLoan[]>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  // Invoice Periods

  getAllInvoicePeriodsByClient(clientContractId: string): Observable<InvoicePeriod[]> {
    this.actionUrl = 'InvoicePeriods/';
    this.Url = this.baseUrl.concat(this.actionUrl, clientContractId);
    return this.http
      .get<InvoicePeriod[]>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  getInvoicePeriodsForDestinationAndDates(destinationEndpointId: number, start: Date, end: Date, includeDisabled = true): Observable<InvoicePeriod[]> {
    this.actionUrl = 'InvoicePeriods/';
    this.Url = this.baseUrl.concat(this.actionUrl, 'GetByDateRange/', destinationEndpointId.toString(), '/', this.stripData.toString());

    const tmp: InvoicingDetails = new InvoicingDetails({
      dateStart: start,
      dateEnd: end,
      includeDisabled: includeDisabled,
    });

    return this.http
     .post(this.Url, tmp, {headers: this.headers})
     .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  getInvoicePeriodByInvoicePeriodId(invoicePeriodId: string): Observable<InvoicePeriod> {
    this.actionUrl = 'InvoicePeriod/';
    this.Url = this.baseUrl.concat(this.actionUrl, invoicePeriodId);
    return this.http
      .get<InvoicePeriod>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  getCurrentInvoicePeriodForClient(clientContractId: string): Observable<InvoicePeriod> {
    this.actionUrl = 'InvoicePeriod/Current/';
    this.Url = this.baseUrl.concat(this.actionUrl, clientContractId);
    return this.http
      .get<InvoicePeriod>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  getPreviousBilledInvoicePeriodForClient(clientContractId: string): Observable<InvoicePeriod> {
    this.actionUrl = 'InvoicePeriod/PreviousBilled/';
    this.Url = this.baseUrl.concat(this.actionUrl, clientContractId);
    return this.http
      .get<InvoicePeriod>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  getLastBilledInvoicePeriodForClient(clientContractId: string): Observable<InvoicePeriod> {
    this.actionUrl = 'InvoicePeriod/LastBilled/';
    this.Url = this.baseUrl.concat(this.actionUrl, clientContractId);
    return this.http
      .get<InvoicePeriod>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }


  /**
   * Get Invoicing Report Loans for Invoice Period
   * @param invoicePeriodId
   * @returns InvoicingReportLoan[]
   */
   getAllInvoicingReportLoansByPeriod(invoicePeriodId: string): Observable<InvoicingReportLoan[]> {
    this.actionUrl = 'InvoicingReportLoans/';
    this.Url = this.baseUrl.concat(this.actionUrl, invoicePeriodId, '/', this.stripData.toString());
    return this.http
      .get<InvoicingReportLoan[]>(this.Url)
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  /**
   * Get Invoicing Report Loans for Invoice Period List
   * @param invoicePeriodId
   * @returns InvoicingReportLoan[]
   */
   getAllInvoicingReportLoansByPeriodList(invoicePeriodIdList: string[]): Observable<InvoicingReportLoan[]> {
    this.actionUrl = 'InvoicingReportLoans/List';
    this.Url = this.baseUrl.concat(this.actionUrl, '/', this.stripData.toString());

    const params = new HttpParams({
      fromObject: {'iPIdList': invoicePeriodIdList},
    });

    return this.http
      .get<InvoicingReportLoan[]>(this.Url, { params })
      .pipe(catchError(err => this.errorHandler(err, this.toastService)));
  }

  /**
   * Get Contract Metric Export - one record per client contract aggregated across every billed
   * invoice period overlapping the date range, with a count per value for each requested metric
   * @param start range start date
   * @param end range end date
   * @param metrics metric identifiers (loan column names or custom json field property names)
   * @param onlyIncludedLoans only count loans flagged Include In Invoice
   * @returns ContractMetricExport[]
   */
  getContractMetricExport(start: Date, end: Date, metrics: string[], onlyIncludedLoans = true): Observable<ContractMetricExport[]> {
    this.actionUrl = 'InvoicingReportLoans/MetricExport';
    this.Url = this.baseUrl.concat(this.actionUrl);

    const params = new HttpParams({
      fromObject: {
        dS: this.toDateOnlyString(start),
        dE: this.toDateOnlyString(end),
        m: metrics,
        iI: onlyIncludedLoans.toString(),
      },
    });

    return this.http
      .get<ContractMetricExport[]>(this.Url, { params })
      .pipe(
        // API returns 204 No Content (null body) when no records exist
        map(data => (data ?? []).map(x => new ContractMetricExport(x))),
        catchError(err => this.errorHandler(err, this.toastService)),
      );
  }

  // local yyyy-MM-dd, avoids toISOString() shifting the date across a UTC boundary
  private toDateOnlyString(date: Date): string {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }
}
